import React from 'react';
import { Box, Button, Chip, Divider, FormControlLabel, Paper, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StayCurrentLandscapeIcon from '@mui/icons-material/StayCurrentLandscape';
import StayCurrentPortraitIcon from '@mui/icons-material/StayCurrentPortrait';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { ISignageModeSettings, ISignageStore, SIGNAGE_MODES, SIGNAGE_ORIENTATIONS, SignageMode, SignageOrientation } from '../../types/signage';

/** The editable settings of a store while they are being changed (not saved yet). */
export interface IStoreDraft {
  /** Shared by the entrance and the exit device. */
  name: string;
  entrance: ISignageModeSettings;
  exit: ISignageModeSettings;
}

const DEFAULT_MODE_SETTINGS: ISignageModeSettings = { playAudio: false, orientation: 'portrait' };

export const draftFromStore = (store: ISignageStore): IStoreDraft => ({
  name: store.name,
  entrance: { ...DEFAULT_MODE_SETTINGS, ...store.entrance },
  exit: { ...DEFAULT_MODE_SETTINGS, ...store.exit },
});

/** A change made in the panel: the shared name and/or settings of the mode being edited. */
export interface IDraftPatch {
  name?: string;
  settings?: Partial<ISignageModeSettings>;
}

export const applyDraftPatch = (draft: IStoreDraft, mode: SignageMode, patch: IDraftPatch): IStoreDraft => ({
  ...draft,
  name: patch.name ?? draft.name,
  [mode]: { ...draft[mode], ...patch.settings },
});

export const isDraftDirty = (draft: IStoreDraft, store: ISignageStore): boolean => {
  const saved = draftFromStore(store);
  const modeChanged = (mode: SignageMode) => draft[mode].playAudio !== saved[mode].playAudio || draft[mode].orientation !== saved[mode].orientation;
  return draft.name.trim() !== saved.name || modeChanged('entrance') || modeChanged('exit');
};

interface StoreSettingsPanelProps {
  store: ISignageStore;
  isDefault: boolean;
  canManage: boolean;
  /** The mode whose device settings are shown. */
  mode: SignageMode;
  draft: IStoreDraft;
  dirty: boolean;
  saving: boolean;
  error?: string;
  /** Receives only what changed, so quick successive edits never overwrite each other. */
  onChange: (patch: IDraftPatch) => void;
  onSave: () => void;
  onReset: () => void;
  onDelete: () => void;
}

const CONTROL_HEIGHT = 56;

// One setting: a label, the control (all the same height so a row lines up) and a short hint.
const Field: React.FC<{
  label: string;
  hint: React.ReactNode;
  children: React.ReactNode;
}> = ({ label, hint, children }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        mb: 0.75,
        fontWeight: 700,
        letterSpacing: '.06em',
        textTransform: 'uppercase',
        color: 'text.secondary',
      }}
    >
      {label}
    </Typography>
    {children}
    <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.75, lineHeight: 1.4 }}>
      {hint}
    </Typography>
  </Box>
);

const StoreSettingsPanel: React.FC<StoreSettingsPanelProps> = ({ isDefault, canManage, mode, draft, dirty, saving, error, onChange, onSave, onReset, onDelete }) => {
  const nameError = !isDefault && !draft.name.trim() ? 'Store name is required' : '';
  const settings = draft[mode];
  const modeLabel = SIGNAGE_MODES.find((option) => option.value === mode)!.label;
  const setSettings = (patch: Partial<ISignageModeSettings>) => onChange({ settings: patch });
  const disabled = !canManage;

  return (
    <Paper sx={{ borderRadius: 2, mb: 2, overflow: 'hidden' }} data-testid="store-settings">
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{
          px: 2.5,
          py: 1.5,
          flexWrap: 'wrap',
          rowGap: 1,
          bgcolor: 'action.hover',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {isDefault ? 'Default store settings' : 'Store settings'} · {modeLabel} device
        </Typography>
        {dirty && <Chip size="small" color="warning" variant="outlined" label="Unsaved changes" />}
        <Box sx={{ flex: 1 }} />
        {canManage ? (
          <>
            <Button onClick={onReset} disabled={!dirty || saving}>
              Reset
            </Button>
            <Button variant="contained" onClick={onSave} disabled={!dirty || saving || !!nameError}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            View only
          </Typography>
        )}
      </Stack>
      <Divider />

      <Box sx={{ p: 2.5 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: isDefault ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))',
            },
            gap: { xs: 2.5, lg: 3 },
            alignItems: 'start',
          }}
        >
          {!isDefault && (
            <Field
              label="Store name"
              hint={
                nameError ? (
                  <Typography variant="caption" color="error">
                    {nameError}
                  </Typography>
                ) : (
                  'Shown in the kiosk header. Shared by the entrance and exit devices.'
                )
              }
            >
              <TextField
                value={draft.name}
                onChange={(e) => onChange({ name: e.target.value })}
                error={!!nameError}
                disabled={disabled}
                placeholder="Store name"
                inputProps={{ maxLength: 100, 'aria-label': 'Store name' }}
                InputProps={{ sx: { height: CONTROL_HEIGHT } }}
                fullWidth
              />
            </Field>
          )}

          <Field label="Device orientation" hint={`How the ${modeLabel.toLowerCase()} device is held.`}>
            <ToggleButtonGroup
              exclusive
              fullWidth
              value={settings.orientation}
              disabled={disabled}
              onChange={(_, value: SignageOrientation | null) => value && setSettings({ orientation: value })}
              sx={{ height: CONTROL_HEIGHT }}
            >
              {SIGNAGE_ORIENTATIONS.map((option) => (
                <ToggleButton key={option.value} value={option.value} sx={{ gap: 1, textTransform: 'none', fontWeight: 600 }}>
                  {option.value === 'portrait' ? <StayCurrentPortraitIcon fontSize="small" /> : <StayCurrentLandscapeIcon fontSize="small" />}
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Field>

          <Field label="Video sound" hint={settings.playAudio ? `Videos play their sound on the ${modeLabel.toLowerCase()} device.` : 'Videos play muted.'}>
            <Box
              sx={{
                height: CONTROL_HEIGHT,
                px: 1.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {settings.playAudio ? <VolumeUpIcon fontSize="small" color="primary" /> : <VolumeOffIcon fontSize="small" color="disabled" />}
              <FormControlLabel
                sx={{ flex: 1, m: 0, ml: 1, justifyContent: 'space-between' }}
                labelPlacement="start"
                control={<Switch checked={settings.playAudio} disabled={disabled} onChange={(e) => setSettings({ playAudio: e.target.checked })} />}
                label="Play the sound of videos"
              />
            </Box>
          </Field>
        </Box>

        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </Box>

      <Divider />
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2.5, py: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {isDefault ? 'Applies to the Default store and to any store without a record of its own, for each device separately. ' : ''}
          The kiosk app picks up changes when it next syncs.
        </Typography>
        {canManage && !isDefault && (
          <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={onDelete} disabled={saving}>
            Delete store
          </Button>
        )}
      </Stack>
    </Paper>
  );
};

export default StoreSettingsPanel;
